/** Discover page — browse and search available courses with category filtering. */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useCourses, useCourseCategories } from '@/shared/api/hooks';
import { Button, PageContainer, SearchField } from '@/interfaces/learner/ui';
import FeaturedCourse, { FeaturedCourseSkeleton } from './FeaturedCourse';
import CategoryFilter from './CategoryFilter';
import CourseCard from './CourseCard';

const EASE = [0.16, 1, 0.3, 1] as const;

export default function DiscoverPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Featured courses (separate query — only public courses appear on discover)
  const { data: featuredData, isLoading: loadingFeatured } = useCourses({
    featured: true,
    visibility: 'public',
    pageSize: 5,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });

  // Filtered course list (only public courses appear on discover)
  const { data: coursesData, isLoading: loadingCourses } = useCourses({
    page,
    pageSize: 12,
    search: search || undefined,
    categoryId: categoryId || undefined,
    visibility: 'public',
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });

  // Categories for filter pills
  const { data: categoriesData } = useCourseCategories();

  const featuredCourses = featuredData?.data ?? [];
  const courses = coursesData?.data ?? [];
  const pagination = coursesData?.pagination;
  const categories = categoriesData?.data ?? [];
  const hasMore = pagination ? page < pagination.totalPages : false;
  const totalCourses = pagination?.total ?? courses.length;

  // Pick one featured course — first featured, or fall back to newest from main list
  const heroCourse = featuredCourses[0] ?? (courses.length > 0 ? courses[0] : null);

  // Only deduplicate when there are enough courses to fill the grid without it
  const gridCourses =
    heroCourse && courses.length > 1 ? courses.filter((c) => c.id !== heroCourse.id) : courses;

  function handleCategoryChange(id: string | null) {
    setCategoryId(id);
    setPage(1);
  }

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value) {
      setSearch('');
      setPage(1);
    } else {
      debounceRef.current = setTimeout(() => {
        setSearch(value);
        setPage(1);
      }, 300);
    }
  }

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  return (
    <PageContainer
      className="relative pb-24"
      style={{
        fontFamily: 'var(--font-sans)',
      }}
    >
      {/* ── Page header ── */}
      <section style={{ paddingTop: 'var(--page-top-spacing)' }}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="min-w-0"
        >
          <div className="flex flex-col gap-2 learner-double:flex-row learner-double:items-end learner-double:justify-between">
            <div className="min-w-0">
              <h1 className="text-section-title" style={{ margin: 0 }}>
                Discover Courses
              </h1>
              <p
                className="text-body mt-1.5"
                style={{
                  maxWidth: '60ch',
                  marginBottom: 0,
                }}
              >
                Browse public courses, filter by category, and jump into your next learning path.
              </p>
            </div>

            <p
              className="text-eyebrow shrink-0"
              style={{
                color: 'var(--color-text-muted)',
                margin: 0,
              }}
            >
              {search || categoryId ? `${gridCourses.length} shown` : `${totalCourses} available`}
            </p>
          </div>
        </motion.div>
      </section>

      {/* ── Featured course hero ── */}
      <section className="relative pt-7 learner-double:pt-8">
        {loadingFeatured && !heroCourse ? (
          <FeaturedCourseSkeleton />
        ) : heroCourse ? (
          <FeaturedCourse course={heroCourse} />
        ) : null}
      </section>

      {/* ── Gradient divider ── */}
      <motion.div
        className="mx-auto my-8"
        style={{
          height: 1,
          maxWidth: '92%',
          background:
            'linear-gradient(90deg, transparent 0%, var(--color-accent-line) 50%, transparent 100%)',
        }}
        initial={{ opacity: 0, scaleX: 0.3 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
      />

      {/* ── Filters: search + categories ── */}
      <motion.section
        className="rounded-[22px] border p-4 learner-double:p-5"
        style={{
          background: 'var(--color-surface-solid)',
          borderColor: 'var(--color-border)',
        }}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.25, ease: EASE }}
      >
        <div className="flex flex-col gap-4 learner-double:flex-row learner-double:items-center learner-double:gap-6">
          <div className="w-full learner-double:max-w-md">
            <SearchField
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search courses..."
            />
          </div>
          <div className="min-w-0 flex-1">
            <CategoryFilter
              categories={categories}
              selectedId={categoryId}
              onSelect={handleCategoryChange}
            />
          </div>
        </div>
      </motion.section>

      {/* ── Course grid ── */}
      <section className="mt-9">
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="text-section-title" style={{ margin: 0 }}>
            All Courses
          </p>
          <p
            className="text-eyebrow"
            style={{
              color: 'var(--color-text-muted)',
              margin: 0,
            }}
          >
            {search || categoryId ? `${gridCourses.length} shown` : `${totalCourses} available`}
          </p>
        </div>

        {loadingCourses && courses.length === 0 ? (
          <GridSkeleton />
        ) : gridCourses.length === 0 ? (
          <EmptyState search={search} categoryId={categoryId} />
        ) : (
          <>
            <AnimatePresence mode="popLayout">
              <motion.div
                key={`${search}-${categoryId}`}
                className="grid gap-5 learner-single:grid-cols-2 learner-double:grid-cols-3"
              >
                {gridCourses.map((course, i) => (
                  <CourseCard key={course.id} course={course} index={i} />
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Show more */}
            {hasMore && (
              <div className="mt-10 flex justify-center">
                <Button variant="ghost" size="md" onPress={() => setPage((p) => p + 1)}>
                  Show more courses
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </PageContainer>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────

function EmptyState({ search, categoryId }: { search: string; categoryId: string | null }) {
  const hasFilters = search || categoryId;
  return (
    <div className="py-16 text-center">
      <p className="text-body" style={{ margin: 0 }}>
        {hasFilters ? 'No courses match your filters' : 'No courses available yet'}
      </p>
      {hasFilters && (
        <p className="text-meta-muted mt-1" style={{ marginBottom: 0 }}>
          Try adjusting your search or category
        </p>
      )}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────

function GridSkeleton() {
  return (
    <div className="grid gap-5 learner-single:grid-cols-2 learner-double:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-2xl"
          style={{
            background: 'var(--color-surface-solid)',
            border: '1px solid var(--color-border)',
          }}
        >
          <div
            className="h-2 w-full animate-pulse"
            style={{ background: 'var(--color-surface-muted)' }}
          />
          <div className="p-5">
            <div
              className="h-5 w-3/4 animate-pulse rounded"
              style={{ background: 'var(--color-surface-muted)' }}
            />
            <div
              className="mt-2 h-4 w-full animate-pulse rounded"
              style={{ background: 'var(--color-surface-muted)' }}
            />
            <div
              className="mt-1 h-4 w-2/3 animate-pulse rounded"
              style={{ background: 'var(--color-surface-muted)' }}
            />
            <div className="mt-4 flex gap-2">
              <div
                className="h-4 w-16 animate-pulse rounded-full"
                style={{ background: 'var(--color-surface-muted)' }}
              />
              <div
                className="h-4 w-12 animate-pulse rounded-full"
                style={{ background: 'var(--color-surface-muted)' }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
